import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// OwnerType stays on the deprecated v1 SDK (@vendasta/smsv2) on purpose. It's a shared
// enum used by owner.service, sms-config.service and several client apps, so moving it
// to v2 is a separate cross-app effort. Everything else here uses the generated v2 SDK.
import { OwnerType } from '@vendasta/smsv2';
import {
  getA2PRegistrationInfo$,
  getLatestA2PRegistrationStatus$,
  upsertA2PRegistrationInfo$,
} from '@vendasta/protos/sms/v2/a2pregistration';
import type { OwnerType as OwnerTypeV2 } from '@vendasta/protos/sms/v2/enums';
import type {
  A2PRegistrationInfo,
  A2PRegistrationStatus,
  CampaignError,
  UpsertA2PRegistrationInfoRequest,
} from '@vendasta/protos/sms/v2/types';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { SmsRegistrationDependencies } from '../../interface';
import { Region } from './sms-registration-form/types';
import { NORTH_AMERICA_COUNTRIES, RegistrationStage } from './a2p-registration.constants';
import { SmsInjectionToken } from '../../token';
import { getRegistrationStage } from './a2p-helper-functions';
import { OwnerService } from '../../common/owner.service';

export interface A2PRegistrationState {
  ownerId: string;
  a2pRegistrationInfo: A2PRegistrationInfo | null;
  a2pRegistrationStatus: A2PRegistrationStatus;
  RegistrationStage: RegistrationStage;
}

@Injectable({
  providedIn: 'root',
})
export class A2PRegistrationService {
  private readonly A2PRegistrationState$$: BehaviorSubject<A2PRegistrationState> =
    new BehaviorSubject<A2PRegistrationState>({
      ownerId: '',
      a2pRegistrationInfo: null,
      a2pRegistrationStatus: {} as A2PRegistrationStatus,
      RegistrationStage: RegistrationStage.NotSubmitted,
    });

  public readonly A2PRegistrationState$: Observable<A2PRegistrationState> = this.A2PRegistrationState$$.asObservable();

  //TODO(MEGA-2359) this should be proxied through RegistrationService that takes in both the regulation status and the a2pRegistrationStatus
  public readonly registrationStage$: Observable<RegistrationStage> = this.A2PRegistrationState$$.asObservable().pipe(
    map((state) => state.RegistrationStage),
  );

  public readonly a2PRegistrationInfo$: Observable<A2PRegistrationInfo | null> =
    this.A2PRegistrationState$$.pipe(map((state) => state.a2pRegistrationInfo));

  public readonly a2pRegistrationStatus$: Observable<A2PRegistrationStatus> = this.A2PRegistrationState$$.pipe(
    map((state) => state.a2pRegistrationStatus),
  );

  public readonly stateOwnerId$: Observable<string> = this.A2PRegistrationState$$.pipe(
    map((state) => state.ownerId),
    distinctUntilChanged(),
  );

  public readonly showExternalBanner$: Observable<boolean> = this.registrationStage$.pipe(
    map((stage) => stage === RegistrationStage.NotSubmitted || stage === RegistrationStage.DataError),
    shareReplay(1),
  );

  private readonly ownerService = inject(OwnerService);
  private readonly config = inject<SmsRegistrationDependencies>(SmsInjectionToken);

  ownerId$ = this.config.ownerId$.pipe(distinctUntilChanged(), shareReplay(1));
  public ownerType: OwnerType = this.config.ownerType;

  // Campaign failure reasons live on messagingCampaign.errors in the v2 status,
  // which the constructor already fetches into state. Surface them only when a
  // campaign has actually failed.
  public readonly campaignErrors$: Observable<CampaignError[]> = combineLatest([
    this.registrationStage$.pipe(distinctUntilChanged()),
    this.a2pRegistrationStatus$,
  ]).pipe(
    map(([stage, status]) =>
      stage === RegistrationStage.CampaignError ? status?.messagingCampaign?.errors ?? [] : [],
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    combineLatest([this.ownerId$, this.config.subaccountID$])
      .pipe(
        switchMap(([ownerId, subaccountId]) => {
          const info$ = getA2PRegistrationInfo$({ ownerId }).pipe(
            map((res) => res.a2pRegistrationInfo ?? ({} as A2PRegistrationInfo)),
            catchError(() => {
              console.error('Error getting A2P registration info');
              return of({} as A2PRegistrationInfo);
            }),
          );

          const status$ =
            !ownerId || !subaccountId
              ? of({} as A2PRegistrationStatus)
              : getLatestA2PRegistrationStatus$({ ownerId, subaccountId }).pipe(
                  map((response) => response.status ?? ({} as A2PRegistrationStatus)),
                  catchError((e) => {
                    console.error('getLatestA2pRegistrationStatus error:', e);
                    return of({} as A2PRegistrationStatus);
                  }),
                );

          return forkJoin([info$, status$]).pipe(map(([info, status]) => [ownerId, info, status] as const));
        }),
        takeUntilDestroyed(),
      )
      .subscribe(([ownerId, a2pInfo, a2pRegistrationStatus]) => {
        this.A2PRegistrationState$$.next({
          ownerId,
          a2pRegistrationInfo: a2pInfo,
          a2pRegistrationStatus: a2pRegistrationStatus,
          RegistrationStage: getRegistrationStage(a2pRegistrationStatus, a2pInfo),
        });
      });
  }

  public async upsertA2pRegistration(request: UpsertA2PRegistrationInfoRequest): Promise<void> {
    request.ownerId = await firstValueFrom(this.ownerId$);
    // v1 OwnerType is numeric; v2 wants the string name. The enum's reverse map
    // (OwnerType[2] === 'OWNER_TYPE_ACCOUNT_GROUP') yields the exact v2 value.
    request.ownerType = OwnerType[this.config.ownerType] as OwnerTypeV2;
    await firstValueFrom(upsertA2PRegistrationInfo$(request));
    this.updateA2pRegistrationInfo(request);
  }

  private updateA2pRegistrationInfo(request: UpsertA2PRegistrationInfoRequest): void {
    const currentState = this.A2PRegistrationState$$.getValue();
    const currentInfo = currentState.a2pRegistrationInfo ?? ({} as A2PRegistrationInfo);
    const updatedInfo = {
      ...currentInfo,
      ...request,
      submitted: request.submitted ? new Date() : currentInfo.submitted,
    } as A2PRegistrationInfo;
    this.A2PRegistrationState$$.next({
      ...currentState,
      a2pRegistrationInfo: updatedInfo,
      RegistrationStage: getRegistrationStage(currentState.a2pRegistrationStatus, updatedInfo),
    });
  }

  getPrefillA2PFormInfo(): Observable<A2PRegistrationInfo> {
    return this.a2PRegistrationInfo$.pipe(
      filter((info): info is A2PRegistrationInfo => info !== null),
      switchMap((a2PRegistrationInfo) => {
        if (Object.keys(a2PRegistrationInfo).length > 0) {
          return of(a2PRegistrationInfo);
        }

        if (this.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP) {
          return this.ownerService.getAccountGroupInfo$().pipe(
            map((accountGroupInfo) => {
              return {
                business: {
                  regionOfOperation: NORTH_AMERICA_COUNTRIES.includes(accountGroupInfo?.napData?.country)
                    ? Region.usaAndCanada
                    : '',
                  streetAddress: accountGroupInfo?.napData?.address,
                  streetAddressSecondary: accountGroupInfo?.napData?.address2,
                  state: accountGroupInfo?.napData?.state,
                  city: accountGroupInfo?.napData?.city,
                  postalCode: accountGroupInfo?.napData?.zip,
                  websiteUrl: accountGroupInfo?.napData?.website,
                },
              } as A2PRegistrationInfo;
            }),
          );
        }

        return of({} as A2PRegistrationInfo);
      }),
    );
  }
}
